import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  BarChart3, 
  Database, 
  FileSpreadsheet, 
  PieChart,
  ChartNoAxesCombined, 
  Download, 
  Eye,
  ArrowRight,
  TrendingUp,
  Users,
  Target
} from "lucide-react";

const Home = () => {
  const skills = [
    { name: "SQL", icon: Database },
    { name: "Python", icon: BarChart3 },
    { name: "PowerBI", icon: ChartNoAxesCombined},
    { name: "Tableau", icon: PieChart },
    { name: "Excel", icon: FileSpreadsheet },
  ];

  const stats = [
    { number: "1", label: "Years Experience", icon: TrendingUp },
    { number: "3", label: "Projects Completed", icon: Target },
  /*{number: "", label: "Happy Clients", icon: Users }*/,
  ];

  const featuredProjects = [
    {
      title: "Cyclistic Bike-Share Analysis",
      description: "Analyzed bike sharing patterns to increase annual memberships",
      tools: ["Python", "Tableau", "SQL"],
      type: "Case Study"
    },
    {
      title: "Vendor Performance Analysis",
      description: "Analyzed vendor performance and inventory to uncover inefficiencies, optimize costs, and improve purchasing strategies.",
      tools: ["SQL", "Python", "Power BI"],
      type: "Business Intelligence"
    },
    {
      title: "Layoffs Analysis",
      description: "Comprehensive analysis of global layoff trends and patterns",
      tools: ["R", "Power BI", "Excel"],
      type: "Data Analysis"
    },
    /*{
      title: "Agriculture Data Analytics",
      description: "Crop yield prediction and optimization analysis",
      tools: ["Python", "Pandas", "Matplotlib"],
      type: "Predictive Analysis"
    }*/
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="space-y-4 sm:space-y-6 text-center lg:text-left" data-aos="fade-right">
              <div className="space-y-2 sm:space-y-3">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
                  Hi, I'm{" "}
                  <span className="text-blue-600 dark:text-blue-400">
                    Jatin Mukati
                  </span>
                </h1>
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-700 dark:text-gray-300">
                  Data Analyst | Turning Data into Insights
                </h2>
              </div>
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-lg mx-auto lg:mx-0 leading-relaxed px-4 lg:px-0">
                Skilled in SQL, Excel, Python, Tableau | Transforming raw data into 
                actionable insights that drive business decisions.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Button asChild size="lg" className="bg-blue-600 text-white hover:bg-blue-800 text-sm sm:text-base">
                  <Link to="/projects">
                    <Eye className="mr-2 h-4 w-4" />
                    View Projects
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-sm sm:text-base">
                  <Link to="/resume">
                    <Download className="mr-2 h-4 w-4" />
                    Download Resume
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative order-first lg:order-last" data-aos="fade-left">
              <div className="w-48 h-48 sm:w-64 sm:h-64 lg:w-72 lg:h-72 mx-auto bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                <div className="w-44 h-44 sm:w-60 sm:h-60 lg:w-68 lg:h-68 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-40 h-40 sm:w-56 sm:h-56 lg:w-64 lg:h-64 rounded-full overflow-hidden border-4 border-blue-400 dark:border-blue-600">
                      <img 
                        src="/Resized.png" 
                        alt="Jatin Mukati" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12" data-aos="fade-up">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Core Skills
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 px-4">
              Technologies and tools I work with
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {skills.map((skill, index) => (
              <Card key={skill.name} className="glassmorphism-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <CardContent className="p-4 sm:p-6 text-center">
                  <skill.icon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-blue-600 dark:text-blue-400 mx-auto mb-3 sm:mb-4" />
                  <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 dark:text-white">
                    {skill.name}
                  </h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gray-50 dark:bg-gray-900 py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={stat.label} className="text-center" data-aos="zoom-in" data-aos-delay={index * 200}>
                <stat.icon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                <div className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Featured Projects
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Some of my recent work
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredProjects.map((project, index) => (
              <Card key={index} className="glassmorphism-card flex flex-col" data-aos="fade-up" data-aos-delay={index * 150}>
                <CardContent className="p-6 flex flex-col flex-grow">
                  <Badge className="mb-4 w-fit">{project.type}</Badge>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tools.map((tool) => (
                      <Badge key={tool} variant="secondary">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-auto">
                    <Button asChild size="lg" variant="outline" className="w-full">
                      <Link to="/projects">
                        View Details
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12" data-aos="fade-up" data-aos-delay="600">
            <Button asChild size="lg">
              <Link to="/projects">
                View All Projects
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 dark:bg-blue-800 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to turn your data into insights?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let's work together to unlock the potential in your data
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="outline" className="hover:bg-white hover:text-blue-600">
              <Link to="/contact">
                Get In Touch
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="hover:bg-white hover:text-blue-600">
              <Link to="/about">
                Learn More About Me
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;